import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { insertSetorSchema, type InsertSetor, type SetorWithVariedade, type VariedadeWithCultura } from "@shared/schema";
import { useCulturas } from "@/hooks/use-culturas";
import { Plus, Map as MapIcon, Pencil, Info } from "lucide-react";
import { AdminLista, AdminListaItem, ALVO_TOQUE } from "@/components/admin/admin-lista";
import { format } from "date-fns";

import { PageHeader, ALVO_TOQUE_BOTAO } from "@/components/layout/page-header";
import { PageToolbar, PageSearch } from "@/components/layout/page-toolbar";
import { AdminTabela } from "@/components/admin/admin-lista";
import {
  DataTable, DataTableHeader, DataTableBody, DataTableRow,
  DataTableHead, DataTableCell, DataTableNum,
} from "@/components/data/data-table";
import { StatusBadge } from "@/components/data/status-badge";
import { EmptyState, NadaEncontrado } from "@/components/data/empty-state";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { combina } from "@/lib/busca";
const FILTRO_TODAS = "__todas";
const FILTRO_SEM_VARIEDADE = "__sem_variedade";

const COLUNAS_SETORES = [null, "8rem", "9rem", "10rem", "7rem", "7rem", "8rem", "8rem", "4rem"];

// ⚠️ Constante de MÓDULO (dep de useMemo dentro do hook).
const PADRAO_BUSCA = { q: "" };

export default function SetoresPage() {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSetor, setEditingSetor] = useState<SetorWithVariedade | null>(null);
  const [filtroCultura, setFiltroCultura] = useState<string>(FILTRO_TODAS);
  // Busca NO CLIENTE, sobre a lista já carregada.
  const { estado, definir } = useEstadoNaUrl(PADRAO_BUSCA);
  const busca = estado.q;

  const { data: setores, isLoading } = useQuery<SetorWithVariedade[]>({
    queryKey: ["/api/setores"],
  });

  const { data: variedades } = useQuery<VariedadeWithCultura[]>({
    queryKey: ["/api/variedades"],
  });

  const { data: culturas } = useCulturas();

  const form = useForm<InsertSetor>({
    resolver: zodResolver(insertSetorSchema),
    defaultValues: {
      nome: "",
      tipo: "PRODUTIVO",
      variedadeId: "",
      areaHectares: "1.00",
      quantidadeMudas: 0,
      dataPlantio: format(new Date(), "yyyy-MM-dd"),
      origemMudas: "",
      ativo: true,
    },
  });

  const tipoAtual = form.watch("tipo") ?? "PRODUTIVO";

  const createMutation = useMutation({
    mutationFn: async (data: InsertSetor) => {
      const response = await apiRequest("POST", "/api/setores", data);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/setores"] });
      toast({ title: "Setor cadastrado!", description: "O setor foi adicionado com sucesso." });
      setDialogOpen(false);
      setEditingSetor(null);
      form.reset();
    },
    onError: (error: any) => {
      toast({ title: "Erro ao cadastrar setor", description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: InsertSetor & { id: string }) => {
      const { id, ...updateData } = data;
      const response = await apiRequest("PATCH", `/api/setores/${id}`, updateData);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/setores"] });
      toast({ title: "Setor atualizado!", description: "As alterações foram salvas com sucesso." });
      setDialogOpen(false);
      setEditingSetor(null);
      form.reset();
    },
    onError: (error: any) => {
      toast({ title: "Erro ao atualizar setor", description: error.message, variant: "destructive" });
    },
  });

  const onSubmit = (data: InsertSetor) => {
    // Limpar campos agronômicos se for serviço
    const payload: InsertSetor = { ...data };
    if ((payload.tipo ?? "PRODUTIVO") === "SERVICO") {
      payload.variedadeId = null;
      payload.areaHectares = null;
      payload.dataPlantio = null;
      payload.quantidadeMudas = 0;
      payload.origemMudas = "";
    }
    if (editingSetor) {
      updateMutation.mutate({ ...payload, id: editingSetor.id });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleEdit = (setor: SetorWithVariedade) => {
    setEditingSetor(setor);
    form.reset({
      nome: setor.nome,
      tipo: setor.tipo ?? "PRODUTIVO",
      variedadeId: setor.variedadeId ?? "",
      areaHectares: setor.areaHectares ?? "1.00",
      quantidadeMudas: setor.quantidadeMudas ?? 0,
      dataPlantio: setor.dataPlantio ?? format(new Date(), "yyyy-MM-dd"),
      origemMudas: setor.origemMudas ?? "",
      ativo: setor.ativo,
    });
    setDialogOpen(true);
  };

  const handleNewSetor = () => {
    setEditingSetor(null);
    form.reset({
      nome: "",
      tipo: "PRODUTIVO",
      variedadeId: "",
      areaHectares: "1.00",
      quantidadeMudas: 0,
      dataPlantio: format(new Date(), "yyyy-MM-dd"),
      origemMudas: "",
      ativo: true,
    });
    setDialogOpen(true);
  };

  // Produtivos primeiro, depois serviço, cada grupo por nome
  const sortedSetores = setores
    ? [...setores].sort((a, b) => {
        const ta = a.tipo ?? "PRODUTIVO";
        const tb = b.tipo ?? "PRODUTIVO";
        if (ta !== tb) return ta === "PRODUTIVO" ? -1 : 1;
        return a.nome.localeCompare(b.nome);
      })
    : [];

  // Resumo por cultura — só setor PRODUTIVO entra (SERVICO não tem cultura).
  // "Sem variedade" é justamente o buraco de cadastro que o resumo existe pra
  // não deixar passar batido (ex.: setor Laranja apontando pra variedade errada).
  const produtivos = sortedSetores.filter(s => (s.tipo ?? "PRODUTIVO") === "PRODUTIVO");
  const resumoPorCultura = new Map<string, { nome: string; setores: number; ha: number }>();
  let semVariedadeCount = 0;
  for (const s of produtivos) {
    if (!s.variedadeId || !s.cultura) { semVariedadeCount++; continue; }
    const atual = resumoPorCultura.get(s.cultura.id) ?? { nome: s.cultura.nome, setores: 0, ha: 0 };
    atual.setores += 1;
    atual.ha += parseFloat(s.areaHectares ?? "0");
    resumoPorCultura.set(s.cultura.id, atual);
  }

  const setoresFiltrados = sortedSetores.filter(s => {
    if (filtroCultura === FILTRO_TODAS) return true;
    if (filtroCultura === FILTRO_SEM_VARIEDADE) {
      return (s.tipo ?? "PRODUTIVO") === "PRODUTIVO" && !s.variedadeId;
    }
    return s.cultura?.id === filtroCultura;
  });

  // Busca NO CLIENTE, depois do filtro de cultura — os dois recortam a mesma lista.
  const visiveis = setoresFiltrados.filter(s => combina(busca, s.nome, s.variedade?.nome, s.cultura?.nome));

  return (
    <div className="space-y-6">
      <PageHeader
        titulo="Setores"
        descricao="Áreas produtivas e de serviço"
        acao={
          <Button onClick={handleNewSetor} className={ALVO_TOQUE_BOTAO} data-testid="button-new-setor">
            <Plus className="h-4 w-4 mr-2" />
            Novo setor
          </Button>
        }
      />

      {/* Resumo por cultura + filtro */}
      {!isLoading && sortedSetores.length > 0 && (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" data-testid="resumo-culturas">
            {Array.from(resumoPorCultura.values()).map(r => (
              <span key={r.nome} className="text-muted-foreground">
                <span className="font-medium text-foreground">{r.nome}</span>: {r.setores} {r.setores === 1 ? "setor" : "setores"} · {r.ha.toFixed(1)} ha
              </span>
            ))}
            {semVariedadeCount > 0 && (
              <span
                className="font-medium text-atencao"
                data-testid="resumo-sem-variedade"
              >
                Sem variedade: {semVariedadeCount}
              </span>
            )}
          </div>
          <Select value={filtroCultura} onValueChange={setFiltroCultura}>
            <SelectTrigger className="w-full md:w-56" data-testid="select-filtro-cultura">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={FILTRO_TODAS}>Todas as culturas</SelectItem>
              {culturas?.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
              ))}
              <SelectItem value={FILTRO_SEM_VARIEDADE}>Sem variedade</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <PageToolbar
        fim={<PageSearch valor={busca} onChange={v => definir({ q: v })} placeholder="Nome" testId="input-busca-setores" />}
      >
        <span />
      </PageToolbar>

      {isLoading ? (
        <PageSkeleton variante="tabela" linhas={6} />
      ) : sortedSetores.length === 0 ? (
        <EmptyState
          icone={MapIcon}
          titulo="Nenhum setor cadastrado"
          descricao="O setor é a unidade de tudo no Rizoma: mangará, colheita, irrigação e diária são sempre de um setor."
          testId="empty-setores"
        />
      ) : !visiveis.length ? (
        busca.trim()
          ? <NadaEncontrado termo={busca} />
          : <EmptyState icone={MapIcon} titulo="Nenhum setor encontrado" descricao="Nenhum setor na cultura selecionada." testId="empty-setores-filtro" />
      ) : (
        <>
          {/* Desktop: tabela */}
          <AdminTabela>
            <DataTable colunas={COLUNAS_SETORES}>
              <DataTableHeader>
                <DataTableRow>
                  <DataTableHead>Nome</DataTableHead>
                  <DataTableHead>Tipo</DataTableHead>
                  <DataTableHead>Variedade</DataTableHead>
                  <DataTableHead>Cultura</DataTableHead>
                  <DataTableHead num>Área (ha)</DataTableHead>
                  <DataTableHead num>Mudas</DataTableHead>
                  <DataTableHead>Plantio</DataTableHead>
                  <DataTableHead>Situação</DataTableHead>
                  <DataTableHead />
                </DataTableRow>
              </DataTableHeader>
              <DataTableBody>
                {visiveis.map(setor => (
                  <DataTableRow
                    key={setor.id}
                    className="cursor-pointer"
                    onClick={() => handleEdit(setor)}
                    data-testid={`setor-row-${setor.id}`}
                  >
                    {/* O nome é a coluna que responde à pergunta da tela. */}
                    <DataTableCell destaque className="truncate">{setor.nome}</DataTableCell>
                    {/* Tipo e cultura são CATEGORIA: outline neutro, sem tom semântico. */}
                    <DataTableCell>
                      <Badge variant="outline" className="text-xs font-normal">
                        {(setor.tipo ?? "PRODUTIVO") === "SERVICO" ? "Serviço" : "Produtivo"}
                      </Badge>
                    </DataTableCell>
                    <DataTableCell className="text-muted-foreground truncate">
                      {setor.variedade?.nome ?? "—"}
                    </DataTableCell>
                    <DataTableCell>
                      {setor.cultura ? (
                        <Badge variant="outline" className="text-xs font-normal" data-testid={`badge-cultura-${setor.id}`}>
                          {setor.cultura.nome}
                        </Badge>
                      ) : (setor.tipo ?? "PRODUTIVO") === "PRODUTIVO" ? (
                        /* Setor produtivo sem variedade É estado: dá para corrigir,
                           e enquanto não corrigir a previsão de colheita fica cega. */
                        <StatusBadge tom="atencao">sem variedade</StatusBadge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </DataTableCell>
                    <DataTableNum className="text-muted-foreground">{setor.areaHectares ?? "—"}</DataTableNum>
                    <DataTableNum className="text-muted-foreground">
                      {setor.quantidadeMudas > 0 ? setor.quantidadeMudas.toLocaleString() : "—"}
                    </DataTableNum>
                    <DataTableNum className="text-left text-muted-foreground">
                      {setor.dataPlantio
                        ? new Date(setor.dataPlantio + "T00:00:00Z").toLocaleDateString("pt-BR", { timeZone: "UTC" })
                        : "—"}
                    </DataTableNum>
                    <DataTableCell>
                      {setor.ativo
                        ? <StatusBadge tom="sucesso" ponto>Ativo</StatusBadge>
                        : <StatusBadge tom="neutro">Inativo</StatusBadge>}
                    </DataTableCell>
                    <DataTableCell className="text-right">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={e => { e.stopPropagation(); handleEdit(setor); }}
                        data-testid={`button-edit-${setor.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </DataTableCell>
                  </DataTableRow>
                ))}
              </DataTableBody>
            </DataTable>
          </AdminTabela>

          {/* Mobile: lista compacta */}
          <AdminLista>
            {visiveis.map(setor => (
              <AdminListaItem
                key={setor.id}
                testId={`setor-item-${setor.id}`}
                onClick={() => handleEdit(setor)}
                atenuado={!setor.ativo}
                identidade={
                  <span className="flex items-center gap-2">
                    <MapIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="truncate">{setor.nome}</span>
                    {(setor.tipo ?? "PRODUTIVO") === "SERVICO" && (
                      <Badge variant="outline" className="text-xs font-normal shrink-0">Serviço</Badge>
                    )}
                  </span>
                }
                meta={
                  (setor.tipo ?? "PRODUTIVO") === "PRODUTIVO" ? (
                    <span className="truncate">
                      {setor.variedade?.nome ?? "—"}
                      {setor.cultura ? ` · ${setor.cultura.nome}` : (
                        <StatusBadge tom="atencao" className="ml-1">sem variedade</StatusBadge>
                      )}
                    </span>
                  ) : undefined
                }
                acoes={
                  <>
                    {setor.ativo
                      ? <StatusBadge tom="sucesso" ponto>Ativo</StatusBadge>
                      : <StatusBadge tom="neutro">Inativo</StatusBadge>}
                    <Button
                      size="icon"
                      variant="ghost"
                      className={ALVO_TOQUE}
                      aria-label={`Editar ${setor.nome}`}
                      onClick={() => handleEdit(setor)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </>
                }
              />
            ))}
          </AdminLista>
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={open => {
        setDialogOpen(open);
        if (!open) { setEditingSetor(null); form.reset(); }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">
              {editingSetor ? "Editar Setor" : "Novo Setor"}
            </DialogTitle>
            <DialogDescription>
              {editingSetor ? "Atualize as informações do setor." : "Cadastre uma nova área da fazenda."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Nome */}
              <div className="space-y-2">
                <Label htmlFor="nome">Nome do Setor *</Label>
                <Input
                  id="nome"
                  placeholder="Ex: A1, Sede, Placa Solar..."
                  data-testid="input-nome"
                  {...form.register("nome")}
                />
                {form.formState.errors.nome && (
                  <p className="text-xs text-destructive">{form.formState.errors.nome.message}</p>
                )}
              </div>

              {/* Tipo */}
              <div className="space-y-2">
                <Label>Tipo *</Label>
                <Select
                  value={tipoAtual}
                  onValueChange={v => form.setValue("tipo", v as "PRODUTIVO" | "SERVICO")}
                >
                  <SelectTrigger data-testid="select-tipo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PRODUTIVO">Produtivo</SelectItem>
                    <SelectItem value="SERVICO">Área de serviço</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Aviso para serviço */}
            {tipoAtual === "SERVICO" && (
              <div className="flex items-start gap-2 rounded-lg border border-info/30 bg-info-suave/50 p-3 text-sm text-info">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <span>Áreas de serviço aparecem apenas no módulo Equipe (tarefas). Não é necessário informar dados agronômicos.</span>
              </div>
            )}

            {/* Campos agronômicos — só para PRODUTIVO */}
            {tipoAtual === "PRODUTIVO" && (
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="variedadeId">Variedade *</Label>
                  <Select
                    value={form.watch("variedadeId") ?? ""}
                    onValueChange={v => form.setValue("variedadeId", v)}
                  >
                    <SelectTrigger data-testid="select-variedade">
                      <SelectValue placeholder="Selecione a variedade" />
                    </SelectTrigger>
                    <SelectContent>
                      {variedades?.filter(v => v.ativo).map(variedade => (
                        <SelectItem key={variedade.id} value={variedade.id}>
                          {variedade.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.formState.errors.variedadeId && (
                    <p className="text-xs text-destructive">{form.formState.errors.variedadeId.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="areaHectares">Área (hectares) *</Label>
                  <Input
                    id="areaHectares"
                    type="number"
                    step="0.01"
                    placeholder="1.00"
                    data-testid="input-area"
                    {...form.register("areaHectares")}
                  />
                  {form.formState.errors.areaHectares && (
                    <p className="text-xs text-destructive">{form.formState.errors.areaHectares.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="quantidadeMudas">Quantidade de Mudas *</Label>
                  <Input
                    id="quantidadeMudas"
                    type="number"
                    min="0"
                    step="1"
                    placeholder="Ex: 500"
                    data-testid="input-quantidade-mudas"
                    {...form.register("quantidadeMudas", { valueAsNumber: true })}
                  />
                  {form.formState.errors.quantidadeMudas && (
                    <p className="text-xs text-destructive">{form.formState.errors.quantidadeMudas.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dataPlantio">Data do Plantio *</Label>
                  <Input
                    id="dataPlantio"
                    type="date"
                    className="font-mono"
                    data-testid="input-data-plantio"
                    {...form.register("dataPlantio")}
                  />
                  {form.formState.errors.dataPlantio && (
                    <p className="text-xs text-destructive">{form.formState.errors.dataPlantio.message}</p>
                  )}
                </div>
              </div>
            )}

            {tipoAtual === "PRODUTIVO" && (
              <div className="space-y-2">
                <Label htmlFor="origemMudas">Origem das Mudas</Label>
                <Textarea
                  id="origemMudas"
                  placeholder="Ex: Viveiro XYZ, Doação, Produção própria..."
                  data-testid="input-origem"
                  {...form.register("origemMudas")}
                />
              </div>
            )}

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <p className="text-sm font-medium">Setor ativo</p>
                <p className="text-xs text-muted-foreground">Setores inativos não aparecem nos dropdowns</p>
              </div>
              <Switch
                checked={form.watch("ativo") ?? true}
                onCheckedChange={v => form.setValue("ativo", v)}
              />
            </div>

            <DialogFooter className="flex-col-reverse md:flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="button-cancel">
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                data-testid="button-submit"
              >
                {(createMutation.isPending || updateMutation.isPending)
                  ? "Salvando..."
                  : editingSetor ? "Salvar Alterações" : "Cadastrar Setor"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
